import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else if (res.status === 401 || res.status === 403) {
        // Only logout on explicit auth failures (invalid/expired token)
        logout();
      } else {
        // For other errors (500, etc), keep the token but clear user
        // This allows retry on next navigation
        console.error('Auth check failed with status:', res.status);
      }
    } catch (err) {
      // Network errors should NOT logout - the token might still be valid
      // Just log the error and keep the existing state
      console.error('Auth network error:', err);
      // Try to keep user logged in with cached token
      // On next successful call, user data will be refreshed
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    let data;
    try {
      data = await res.json();
    } catch {
      // Response body may be consumed by platform interceptor
      if (!res.ok) {
        throw new Error('Invalid email or password');
      }
      throw new Error('Login failed');
    }
    
    if (!res.ok) {
      throw new Error(data?.detail || 'Invalid email or password');
    }
    
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, fullName) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, full_name: fullName })
    });
    
    let data;
    try {
      data = await res.json();
    } catch {
      // Response body may be consumed by platform interceptor
      if (!res.ok) {
        throw new Error('Registration failed');
      }
      throw new Error('Registration failed');
    }
    
    if (!res.ok) {
      throw new Error(data?.detail || 'Registration failed');
    }
    
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  const isAdmin = user?.role === 'admin';
  const isSalesRep = user?.role === 'sales_rep';
  const isStaff = isAdmin || isSalesRep;

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser, isAdmin, isSalesRep, isStaff }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
